/**
 * ANSWER, OR GIVE UP SAYING SO — a bound on a promise that may never settle.
 *
 * A promise that rejects is a failure this product can talk about; a promise that never settles is
 * one it cannot see. `livekitConnector` has exactly that shape at its most important moment: the SDK
 * publishes a microphone by bringing up a transport, and when that transport never comes up the call
 * simply waits. The press then neither opens a microphone nor produces a sentence, which is the one
 * outcome §0 says a live control must never have — and it is precisely what a browser gate printed
 * three times: *"the press did neither: no microphone and nothing said — console=[]"*, with the
 * empty console proving nothing was thrown.
 *
 * `undefined` for "no answer in time" rather than a rejection, because that is what the SDK call it
 * wraps already means by `undefined`: nothing was published. The caller decides what to do about it,
 * and in this product's case it retries to a deadline and then tells the reader.
 *
 * A REJECTION STILL TRAVELS. Racing does not swallow it: `work` is awaited here, so a real error —
 * a device another program holds, a permission dismissed — reaches the caller as it always did.
 * What is added is only the case where nothing arrives at all.
 *
 * The timer is cleared either way, so a page that leaves a room does not hold one per press.
 */
export async function answeredWithin<T>(work: Promise<T>, ms: number): Promise<T | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<undefined>(resolve => {
    timer = setTimeout(() => resolve(undefined), ms);
  });
  try {
    return await Promise.race([work, late]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
